/**
 * One small store the offline UI reads from. No provider: the TopBar, the
 * queue sheet and the banner all subscribe to the same module, so they work
 * wherever they are mounted (and in tests, on their own).
 */

import { useSyncExternalStore } from 'react';
import type { CacheStats } from './cache';
import { cacheStats } from './cache';
import { lastSyncAt, listItems } from './outbox';
import type { OutboxItem, StaleNotice } from './types';

export interface OfflineState {
  /** what the browser says, and what our own requests have shown */
  online: boolean;
  items: OutboxItem[];
  waiting: number;
  blocked: number;
  syncing: boolean;
  lastSyncAt?: number;
  /** the most recent stored copy the app served instead of live data */
  stale: StaleNotice | null;
  /** a newer build of the app is installed and waiting */
  updateReady: boolean;
  cache: CacheStats | null;
  /** IndexedDB missing (private mode, ancient browser) */
  unavailable: boolean;
}

const initialState: OfflineState = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  items: [],
  waiting: 0,
  blocked: 0,
  syncing: false,
  stale: null,
  updateReady: false,
  cache: null,
  unavailable: false,
};

let state: OfflineState = initialState;
const listeners = new Set<() => void>();

export const getOfflineState = (): OfflineState => state;

export function setOfflineState(patch: Partial<OfflineState>): void {
  const next = { ...state, ...patch };
  const changed = (Object.keys(patch) as (keyof OfflineState)[]).some(
    (key) => next[key] !== state[key],
  );
  if (!changed) return;
  state = next;
  listeners.forEach((listener) => listener());
}

export function subscribeOffline(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Re-read the queue and the cache size from IndexedDB. */
export async function refreshOfflineState(): Promise<void> {
  try {
    const [items, stats, syncedAt] = await Promise.all([
      listItems(),
      cacheStats(),
      lastSyncAt(),
    ]);
    setOfflineState({
      items,
      waiting: items.filter((item) => item.status !== 'blocked').length,
      blocked: items.filter((item) => item.status === 'blocked').length,
      cache: stats,
      lastSyncAt: syncedAt,
      unavailable: false,
    });
  } catch {
    setOfflineState({ unavailable: true });
  }
}

/** For tests: back to a clean slate. */
export function resetOfflineState(): void {
  state = { ...initialState, online: true };
  listeners.forEach((listener) => listener());
}

export const useOfflineState = (): OfflineState =>
  useSyncExternalStore(subscribeOffline, getOfflineState, getOfflineState);
